import { useState } from "react";
import { Check, RefreshCw, Sparkles } from "lucide-react";
import { toast } from "sonner";
import { http, errMsg, streamGenerate } from "../lib/api";
import { Dialog, DialogContent, DialogTitle, DialogDescription } from "./ui/dialog";
import { CopyButton, Stamp } from "./Bits";

const PLATFORMS = ["All", "Instagram", "TikTok", "Facebook", "X", "Email", "Other"];

const dateFor = (start, day) => {
  if (!start) return "";
  const d = new Date(`${start}T12:00:00`);
  d.setDate(d.getDate() + day - 1);
  return d.toLocaleDateString(undefined, { weekday: "short", day: "numeric", month: "short" });
};

function DayDialog({ book, item, onClose, setBook }) {
  const [draft, setDraft] = useState(item.content);
  const [live, setLive] = useState(null);
  const patch = async (body) => {
    try {
      const { data } = await http.patch(`/books/${book.id}/plan/${item.day}`, body);
      setBook({ ...book, plan: data.plan });
    } catch (e) {
      toast.error(errMsg(e));
    }
  };
  const regen = async () => {
    setLive("");
    try {
      const updated = await streamGenerate(book.id, "plan-day", { day: item.day }, (d) => setLive((p) => p + d));
      setBook(updated);
      setDraft(updated.plan.find((p) => p.day === item.day).content);
    } catch (e) {
      toast.error(e.message);
    } finally {
      setLive(null);
    }
  };
  return (
    <Dialog open onOpenChange={(o) => !o && onClose()}>
      <DialogContent className="max-w-2xl bg-[#FDFBF7] border-[#121210] rounded-none max-h-[90vh] overflow-y-auto" data-testid="plan-day-dialog">
        <p className="label-mono">Day {item.day} · {dateFor(book.plan_start, item.day)} · {item.platform}</p>
        <DialogTitle className="font-display text-3xl font-normal">{item.title}</DialogTitle>
        <DialogDescription className="sr-only">Plan item details</DialogDescription>
        {live !== null ? (
          <pre className="font-type text-[0.8rem] whitespace-pre-wrap bg-[#F6F2EA] border border-[#E5DEC9] p-5 caret" data-testid="plan-day-stream">{live || "Preparing a new version…"}</pre>
        ) : (
          <textarea rows={10} className="input-paper leading-relaxed" value={draft} onChange={(e) => setDraft(e.target.value)} data-testid="plan-day-content-textarea" />
        )}
        <div className="flex flex-wrap gap-2 items-center justify-between">
          <div className="flex flex-wrap -ml-2.5">
            <CopyButton text={draft} testid="plan-day-copy-button" />
            <button className="btn-ghost" onClick={regen} disabled={live !== null} data-testid="plan-day-regenerate-button"><RefreshCw size={12} /> Try another version</button>
            {draft !== item.content && <button className="btn-ghost !text-[#C84B31]" onClick={() => patch({ content: draft })} data-testid="plan-day-save-button">Save edit</button>}
          </div>
          <button className={item.done ? "btn-secondary" : "btn-primary"} onClick={() => patch({ done: !item.done })} data-testid="plan-day-done-toggle">
            <Check size={13} /> {item.done ? "Done — undo" : "Mark as done"}
          </button>
        </div>
      </DialogContent>
    </Dialog>
  );
}

function DayCell({ book, item, dim, onOpen }) {
  return (
    <button onClick={onOpen} data-testid={`plan-day-${item.day}-cell`}
      className={`text-left p-4 min-h-[9.5rem] border border-[#E5DEC9] -ml-px -mt-px flex flex-col gap-2 transition-colors hover:bg-[#F6F2EA] hover:border-[#121210] hover:z-10 relative ${dim ? "opacity-25" : ""} ${item.done ? "bg-[#F6F2EA]" : "bg-white/40"}`}>
      <div className="flex justify-between items-start">
        <span className="font-display text-3xl leading-none">{item.day}</span>
        {item.done ? <span className="w-5 h-5 bg-[#C84B31] text-white flex items-center justify-center" data-testid={`plan-day-${item.day}-done`}><Check size={12} /></span> : <span className="font-type text-[0.6rem] text-[#8E8879] uppercase">{dateFor(book.plan_start, item.day)}</span>}
      </div>
      <span className="font-type text-[0.6rem] uppercase tracking-wider text-[#C84B31]">{item.type} · {item.platform}</span>
      <span className={`text-sm leading-snug line-clamp-3 ${item.done ? "line-through text-[#8E8879]" : ""}`}>{item.title}</span>
    </button>
  );
}

export default function PlanCalendar({ book, setBook }) {
  const [live, setLive] = useState(null);
  const [filter, setFilter] = useState("All");
  const [openDay, setOpenDay] = useState(null);
  const plan = book.plan || [];
  const done = plan.filter((p) => p.done).length;

  const generate = async () => {
    if (plan.length && !window.confirm("Replace the whole plan with a new one? Ticked items will be reset.")) return;
    setLive("");
    try {
      for (const part of [0, 1, 2, 3, 4, 5]) {
        setBook(await streamGenerate(book.id, "plan", { part }, (d) => setLive((p) => p + d)));
      }
    } catch (e) {
      toast.error(e.message);
    } finally {
      setLive(null);
    }
  };

  if (live !== null || !plan.length) {
    return (
      <div className="grid lg:grid-cols-12 gap-10" data-testid="plan-empty">
        <div className="lg:col-span-4">
          <h2 className="font-display text-3xl sm:text-4xl">Thirty days of being seen.</h2>
          <p className="mt-4 text-[#5C584E] leading-relaxed">One action a day: posts for Instagram, TikTok, Facebook and X, reviewer outreach, newsletter emails and free promotion ideas. Every post is ready to share.</p>
          {!book.kit?.hook && <p className="mt-4 font-hand text-xl text-[#C84B31]">Tip: create the Positioning section first for a sharper plan.</p>}
          <button className="btn-primary mt-8" onClick={generate} disabled={live !== null} data-testid="plan-generate-button"><Sparkles size={14} /> {live !== null ? "Preparing your month…" : "Create my 30-day plan"}</button>
        </div>
        {live !== null && <pre className="lg:col-span-8 font-type text-[0.8rem] whitespace-pre-wrap bg-[#F6F2EA] border border-[#E5DEC9] p-6 caret max-h-[34rem] overflow-y-auto" data-testid="plan-stream">{live || "Planning your month…"}</pre>}
      </div>
    );
  }

  const openItem = plan.find((p) => p.day === openDay);
  return (
    <div data-testid="plan-calendar">
      <div className="flex flex-col lg:flex-row lg:items-end justify-between gap-6">
        <div>
          <p className="label-mono">Progress</p>
          <p className="font-display text-4xl mt-1" data-testid="plan-progress">{done}<span className="text-[#8E8879]">/{plan.length}</span> done</p>
          <div className="w-64 h-1.5 bg-[#E5DEC9] mt-3"><div className="h-full bg-[#C84B31] transition-all" style={{ width: `${(done / plan.length) * 100}%` }} /></div>
        </div>
        <div className="flex flex-wrap gap-2.5 items-center">
          {PLATFORMS.map((p) => <Stamp key={p} active={filter === p} onClick={() => setFilter(p)} testid={`plan-filter-${p.toLowerCase()}`}>{p}</Stamp>)}
          <button className="btn-ghost ml-2" onClick={generate} data-testid="plan-regenerate-all-button"><RefreshCw size={12} /> New plan</button>
        </div>
      </div>
      <div className="grid grid-cols-2 sm:grid-cols-3 lg:grid-cols-6 mt-10 pl-px pt-px">
        {plan.map((item) => <DayCell key={item.day} book={book} item={item} dim={filter !== "All" && item.platform !== filter} onOpen={() => setOpenDay(item.day)} />)}
      </div>
      {openItem && <DayDialog key={openItem.day + openItem.title} book={book} item={openItem} setBook={setBook} onClose={() => setOpenDay(null)} />}
    </div>
  );
}
